import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, MapPin, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { NEIGHBORHOOD_DATA, COVERAGE_AREAS } from '../data/mockData';

const API = process.env.REACT_APP_BACKEND_URL;

// Tiny module-level cache so we don't re-fetch the same neighborhood image
// every time the page mounts or the user navigates back. Lives for the
// duration of the page session.
const _imageCache = {};

// Hero image hook: starts with the static `fallback` from mockData, then asks
// the backend for a current MLS listing photo from that city. If DDF returns a
// live listing photo, we swap in the fresh image. If DDF has no inventory for
// that city, the static fallback stays.
const useNeighborhoodImage = (cityId, fallback) => {
  const [src, setSrc] = useState(_imageCache[cityId] || fallback);
  useEffect(() => {
    if (_imageCache[cityId]) {
      setSrc(_imageCache[cityId]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/ddf/neighborhood-image/${encodeURIComponent(cityId)}`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !data.image) return;
        // Only override the per-city fallback from mockData when the backend
        // returns a REAL listing photo. If it returns its own generic fallback
        // (source === 'fallback'), keep the mockData image — otherwise every
        // neighborhood with no active listings ends up showing the same photo.
        if (data.source !== 'ddf') return;
        _imageCache[cityId] = data.image;
        setSrc(data.image);
      } catch (_) {
        // keep fallback
      }
    })();
    return () => { cancelled = true; };
  }, [cityId]);
  return src;
};

const NeighborhoodImg = ({ cityId, alt, fallback }) => {
  const src = useNeighborhoodImage(cityId, fallback);
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      onError={(e) => { e.currentTarget.src = fallback; }}
    />
  );
};

const NeighbourhoodsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Neighbourhood Guides</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Explore market trends, community insights, and discover what makes each area special
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Cities */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <MapPin className="text-red-600" />
            Cities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COVERAGE_AREAS.filter(a => a.type === 'city').map((area) => {
              const data = NEIGHBORHOOD_DATA[area.id];
              if (!data) return null;
              
              return (
                <Link key={area.id} to={`/neighbourhoods/${area.id}`}>
                  <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 h-full border-0">
                    <div className="relative h-48 overflow-hidden">
                      <NeighborhoodImg cityId={area.id} alt={data.name} fallback={data.image} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="font-bold text-xl text-white mb-1">{data.name}</h3>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-600">
                            Avg. ${(data.avgPrice / 1000).toFixed(0)}K
                          </Badge>
                          <Badge variant="outline" className="text-white border-white/50">
                            <TrendingUp size={12} className="mr-1" />
                            {data.priceChange > 0 ? '+' : ''}{data.priceChange}%
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">{data.description}</p>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                        <div className="bg-gray-100 rounded p-2">
                          <div className="font-bold text-gray-900">{data.activeListings}</div>
                          <div className="text-gray-500">Listings</div>
                        </div>
                        <div className="bg-gray-100 rounded p-2">
                          <div className="font-bold text-gray-900">{data.avgDaysOnMarket}</div>
                          <div className="text-gray-500">Avg Days</div>
                        </div>
                        <div className="bg-gray-100 rounded p-2">
                          <div className="font-bold text-gray-900">{data.schoolRating}</div>
                          <div className="text-gray-500">Schools</div>
                        </div>
                      </div>
                      <div className="flex items-center text-red-600 font-medium text-sm group-hover:text-red-700">
                        View Guide
                        <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* All cities are shown in the Cities section above */}
      </div>
    </div>
  );
};

export default NeighbourhoodsPage;
